import { useMemo, useState } from "react";
import { toast } from "sonner";
import { MACHINES, DELIVERY, PAYMENTS, fmt, calculate } from "@/lib/pricing";
import { usePricing } from "@/hooks/usePricing";
import { http, inputCls } from "@/lib/admin";
import { Plus, Trash2, ArrowLeft, FileDown } from "lucide-react";

const emptyItem = () => ({ machine_type: "reformer", quantity: 1, delivery: "d96", payment: "lease_24" });

const EMPTY = { lead_id: null, name: "", company: "", email: "", phone: "", items: [emptyItem()], valid_days: 14, notes: "" };

export function QuoteEditor({ initial, quoteId, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}), items: initial?.items?.length ? initial.items : [emptyItem()] });
  const [saving, setSaving] = useState(false);
  const pricing = usePricing();

  const rows = useMemo(
    () => form.items.map((it) => ({ ...it, calc: calculate(it.machine_type, it.quantity, it.delivery, it.payment, pricing) })),
    [form.items, pricing]
  );
  const totals = rows.reduce(
    (t, r) => ({
      down: t.down + r.calc.downPayment,
      monthly: t.monthly + r.calc.monthly,
      oneTime: t.oneTime + (r.calc.months ? 0 : r.calc.total),
      grand: t.grand + r.calc.total,
    }),
    { down: 0, monthly: 0, oneTime: 0, grand: 0 }
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setItem = (i, patch) => set({ items: form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  const removeItem = (i) => set({ items: form.items.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = quoteId ? await http.put(`/quotes/${quoteId}`, form) : await http.post("/quotes", form);
      toast.success(`Árajánlat ${data.number} elmentve`);
      onSaved(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Sikertelen mentés – ellenőrizze az adatokat");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="quote-editor">
      <button type="button" onClick={onCancel} data-testid="quote-editor-back"
        className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-muted)] hover:text-[var(--terra)] mb-5">
        <ArrowLeft className="w-3.5 h-3.5" /> Vissza az árajánlatokhoz
      </button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow text-[var(--terra)]">{quoteId ? "Árajánlat szerkesztése" : "Új árajánlat"}</p>
          <h2 className="font-serif-d font-semibold text-2xl mt-1">{form.name || "Ügyfél adatai"}</h2>
          {form.lead_id && <p className="text-xs text-[var(--ink-muted)] mt-1">Leadből indítva – az adatok automatikusan kitöltve.</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <section className="bg-white border border-[var(--line)] rounded-2xl p-6">
            <p className="eyebrow text-[var(--ink-muted)] mb-4">Ügyfél</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required data-testid="quote-name" placeholder="Név *" value={form.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} />
              <input data-testid="quote-company" placeholder="Stúdió / cég" value={form.company} onChange={(e) => set({ company: e.target.value })} className={inputCls} />
              <input type="email" data-testid="quote-email" placeholder="E-mail" value={form.email} onChange={(e) => set({ email: e.target.value })} className={inputCls} />
              <input data-testid="quote-phone" placeholder="Telefon" value={form.phone} onChange={(e) => set({ phone: e.target.value })} className={inputCls} />
            </div>
          </section>

          <section className="bg-white border border-[var(--line)] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow text-[var(--ink-muted)]">Tételek</p>
              <button type="button" data-testid="quote-add-item" onClick={() => set({ items: [...form.items, emptyItem()] })}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--terra)] hover:underline">
                <Plus className="w-3.5 h-3.5" /> Tétel hozzáadása
              </button>
            </div>
            <div className="space-y-3">
              {rows.map((r, i) => (
                <div key={i} data-testid={`quote-item-${i}`} className="border border-[var(--line)] rounded-xl p-4 bg-[var(--stone-bg)]">
                  <div className="grid sm:grid-cols-12 gap-3">
                    <Select className="sm:col-span-4" testId={`quote-item-${i}-machine`} value={r.machine_type} options={MACHINES} onChange={(v) => setItem(i, { machine_type: v })} />
                    <input type="number" min={1} max={50} data-testid={`quote-item-${i}-qty`} value={r.quantity}
                      onChange={(e) => setItem(i, { quantity: Math.min(50, Math.max(1, Number(e.target.value) || 1)) })}
                      className={`${inputCls} sm:col-span-2 font-mono-d`} />
                    <Select className="sm:col-span-3" testId={`quote-item-${i}-delivery`} value={r.delivery} options={DELIVERY} onChange={(v) => setItem(i, { delivery: v })} />
                    <Select className="sm:col-span-3" testId={`quote-item-${i}-payment`} value={r.payment} options={PAYMENTS} onChange={(v) => setItem(i, { payment: v })} />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-xs">
                    <p className="text-[var(--ink-soft)]" data-testid={`quote-item-${i}-summary`}>
                      {r.calc.months
                        ? <>Kezdő: <b>{fmt(r.calc.downPayment)}</b> · Havi: <b>{fmt(r.calc.monthly)}</b> × {r.calc.months} hó</>
                        : <>Egyösszegű: <b>{fmt(r.calc.total)}</b></>}
                      {r.calc.deliveryFee > 0 && <> · Expressz díj: {fmt(r.calc.deliveryFee)}</>}
                    </p>
                    {form.items.length > 1 && (
                      <button type="button" data-testid={`quote-item-${i}-remove`} onClick={() => removeItem(i)}
                        className="inline-flex items-center gap-1 text-red-600 hover:underline">
                        <Trash2 className="w-3.5 h-3.5" /> Eltávolítás
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border border-[var(--line)] rounded-2xl p-6">
            <p className="eyebrow text-[var(--ink-muted)] mb-4">Egyéb</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-xs text-[var(--ink-muted)] block mb-1.5">Érvényesség (nap)</span>
                <input type="number" min={1} max={120} data-testid="quote-valid-days" value={form.valid_days}
                  onChange={(e) => set({ valid_days: Math.min(120, Math.max(1, Number(e.target.value) || 1)) })} className={`${inputCls} font-mono-d`} />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs text-[var(--ink-muted)] block mb-1.5">Személyes üzenet az ajánlaton (opcionális)</span>
                <textarea rows={3} data-testid="quote-notes" value={form.notes} onChange={(e) => set({ notes: e.target.value })}
                  placeholder="Pl. Köszönjük érdeklődését, az alábbi konstrukciót a telefonon egyeztetettek alapján állítottuk össze..." className={inputCls} />
              </label>
            </div>
          </section>
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-[var(--ink)] text-[#faf8f5] rounded-2xl p-6 sticky top-24" data-testid="quote-totals">
            <p className="eyebrow text-[#8c8882]">Összesítés</p>
            <div className="mt-4 space-y-2.5 text-sm">
              <Row k="Gépek" v={`${rows.reduce((s, r) => s + r.quantity, 0)} db`} />
              {totals.down > 0 && <Row k="Kezdő befizetés" v={fmt(totals.down)} />}
              {totals.monthly > 0 && <Row k="Havi részlet" v={`${fmt(totals.monthly)} / hó`} accent />}
              {totals.oneTime > 0 && <Row k="Egyösszegű tételek" v={fmt(totals.oneTime)} />}
            </div>
            <div className="border-t border-[var(--line-dark)] mt-4 pt-4">
              <p className="eyebrow text-[#8c8882]">Konstrukció teljes értéke</p>
              <p className="font-serif-d font-semibold text-3xl mt-1" data-testid="quote-grand-total">{fmt(totals.grand)}</p>
              <p className="text-[10px] text-[#8c8882] mt-1">Nettó árak</p>
            </div>
            <button type="submit" disabled={saving} data-testid="quote-save-btn"
              className="btn-terra w-full rounded-xl py-3 text-sm font-semibold mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <FileDown className="w-4 h-4" /> {saving ? "Mentés..." : "Mentés és PDF előnézet"}
            </button>
          </div>
        </aside>
      </div>
    </form>
  );
}

const Select = ({ value, options, onChange, testId, className = "" }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} ${className}`}>
    {Object.entries(options).map(([k, o]) => (
      <option key={k} value={k}>{o.label}{o.sub ? ` – ${o.sub}` : ""}</option>
    ))}
  </select>
);

const Row = ({ k, v, accent }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#8c8882]">{k}</span>
    <span className={accent ? "text-[var(--terra)] font-semibold" : "text-[#d8d4ce]"}>{v}</span>
  </div>
);
